// Group all rules of a raw event
import { uniqueId } from 'lodash';
import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';

import { useGetBlockEventsName, useGetMappedStatusEvent } from '@api/emp/queryHooks';
import { EPanelOpenMethod } from '@components/block-event-single/store';
import { AutoCompleteInput } from '@components/common/AutoCompleteInput';
import { buildOptionsWithTag } from '@components/common/AutoCompleteInput/buildOptionsWithTag';
import LogicDivider from '@components/common/LogicDivider';
import LogicSelector from '@components/common/LogicSelector';
import Label from '@components/common/NumberLabel';
import { useQueryDimensionData } from '@components/mapping-panel/api';
import AddRuleButton from '@components/mapping-panel/components/AddRuleButton';
import {
  AddRuleButtonWrapper,
  EventName,
  InputBox,
  Main,
  Operator,
  Prefix,
  RuleList,
  TopBar,
  TopBarLeft,
} from '@components/mapping-panel/components/RuleBuilder/RuleGroup/styled';
import type { IProps } from '@components/mapping-panel/components/RuleBuilder/RuleGroup/typed';
import RuleRow from '@components/mapping-panel/components/RuleBuilder/RuleRow';
import { Icon } from '@components/metrics-builder/components/MetricList';
import DeleteActiveSvg from '@icons/mapping-panel/delete-active-icon.svg';
import DeleteSvg from '@icons/mapping-panel/delete-icon.svg';
import { blockedCanMapWhiteList } from '@src/common/constant/emp';
import { mappedEventDimensionType } from '@src/common/constant/enum';

import { EInputType } from '../KeyValueBuilder/typed';
import { validateString } from '@src/emp-3.0/utils';
import { useTrackMappedEventSensorStore } from '@components/mapping-panel/store/sensorStore';

const RuleGroup: React.FC<IProps> = (props) => {
  const {
    mode,
    activeIndices,
    data,
    orIndex,
    onDeleteRuleGroup,
    onDeleteRuleItem,
    onAddOrRuleItem,
    onAddAndRuleItem,
    onChange,
    handleLogicChange,
    updateActiveIndices,
    onDragEnd,
    showPadding,
  } = props;
  const { setNameEventOptions } = useTrackMappedEventSensorStore((state) => state);

  const isViewMode = mode === EPanelOpenMethod.VIEW;

  const active = orIndex === activeIndices?.orIndex && !!activeIndices?.ruleItems && !isViewMode;
  const hasLogicSelector = orIndex !== 0;

  const [eventOptions, setEventOptions] = useState<any[]>([]);
  const [eventNameSearchText, setEventNameSearchText] = useState<string>('');

  const { data: mappedEventNameData } = useGetMappedStatusEvent('event_name');
  const { data: blockedEventsName } = useGetBlockEventsName();
  const { data: eventNameData, isFetching: isEventNameLoading } = useQueryDimensionData(
    {
      fields: [mappedEventDimensionType.all_event_name],
      filters: [],
    },
    500,
  );

  const { failed: isIllegalityName } = validateString(data?.eventName);

  useEffect(() => {
    const options = buildOptionsWithTag(
      eventNameData || [],
      mappedEventDimensionType.all_event_name,
      eventNameSearchText,
      mappedEventNameData?.data,
      blockedEventsName,
      undefined,
      undefined,
      blockedCanMapWhiteList,
    );
    setEventOptions(options);
    setNameEventOptions(options);
  }, [eventNameData, mappedEventNameData?.data, blockedEventsName, eventNameSearchText]);

  const handleKeyValueActiveChange = (
    subAndIndex: number,
    subOrIndex: number,
    type: EInputType,
    active: boolean,
  ) => {
    if (type === EInputType.KEY) {
      updateActiveIndices(undefined, [{ subAndIndex, subOrIndex, tagKey: active }]);
      return;
    }
    updateActiveIndices(undefined, [{ subAndIndex, subOrIndex, tagValue: active }]);
  };

  const items: JSX.Element[] = [];
  let count = 0;
  data.rules.forEach((andGroup, subAndIndex) => {
    if (subAndIndex !== 0) {
      items.push(
        <LogicDivider
          disabled={isViewMode}
          key={uniqueId('LogicDivider_')}
          value="AND"
          onChange={() => handleLogicChange(subAndIndex)}
          onDelete={() => handleLogicChange(subAndIndex)}
        />,
      );
    }
    const andGroupRules = andGroup.map((ruleItem, subOrIndex) => {
      count++;
      const rowLabel = `${orIndex + 1}.${count}`;
      const hasBorderBottom = andGroup?.length - 1 !== subOrIndex;
      const itemActiveIndices = activeIndices?.ruleItems?.find(
        (i) => i.subAndIndex === subAndIndex && i.subOrIndex === subOrIndex,
      );
      return (
        <RuleRow
          disabled={isViewMode}
          key={rowLabel}
          eventName={data.eventName}
          index={subOrIndex}
          rowLabel={rowLabel}
          data={ruleItem}
          showLogicOperator={subOrIndex !== 0}
          itemActiveIndices={active ? itemActiveIndices : undefined}
          onActive={() =>
            updateActiveIndices(undefined, [
              { subAndIndex, subOrIndex, tagKey: true, tagValue: true },
            ])
          }
          onDelete={() => {
            onDeleteRuleItem?.(subAndIndex, subOrIndex);
            updateActiveIndices(false, []);
          }}
          onChange={(type, value) => onChange?.(subAndIndex, subOrIndex, type, value)}
          onLogicChange={() => handleLogicChange(subAndIndex, subOrIndex)}
          onKeyValueActiveChange={(...rest) =>
            handleKeyValueActiveChange(subAndIndex, subOrIndex, ...rest)
          }
          style={hasBorderBottom ? undefined : { borderBottom: 'none' }}
        />
      );
    });

    const droppableId = JSON.stringify({ orIndex, subAndIndex });
    const droppableArea = (
      <Droppable droppableId={droppableId} key={droppableId} isDropDisabled={isViewMode}>
        {(provided, snapshot) => (
          <RuleList
            ref={provided.innerRef}
            {...provided.droppableProps}
            isDraggingOver={snapshot.isDraggingOver}
          >
            {andGroupRules}
            {provided.placeholder}
          </RuleList>
        )}
      </Droppable>
    );
    items.push(droppableArea);
  });

  const handelDragEnd = (...args) => {
    onDragEnd?.(args[0], args[1]);
  };

  const handleEventNameChange = (value) => {
    setEventNameSearchText(value);
    onChange?.(-1, -1, EInputType.EVENT_NAME, value);
  };

  return (
    <DragDropContext onDragEnd={handelDragEnd}>
      <div onClick={() => updateActiveIndices(false, [])}>
        <TopBar>
          <TopBarLeft>
            <Prefix type="Group">
              {hasLogicSelector ? <LogicSelector value="OR" active={active} /> : null}
              <Label value={orIndex + 1} square active={active} />
            </Prefix>
            <InputBox>
              <EventName active={active}>Event Name *</EventName>
              <Operator>=</Operator>
              <AutoCompleteInput
                data-test-id={`event_name_${orIndex + 1}`}
                disabled={isViewMode}
                mode={'autoComplete'}
                active={active && activeIndices?.eventName}
                validateStatus={isIllegalityName ? 'error' : undefined}
                value={data.eventName}
                options={eventOptions}
                onChange={handleEventNameChange}
                onFocus={() => updateActiveIndices(true, [])}
                onBlur={() => updateActiveIndices(false, [])}
                loading={isEventNameLoading}
                style={{ flexGrow: 0.5 }}
                onSearch={(text) => {
                  setEventNameSearchText(text);
                }}
              />
            </InputBox>
          </TopBarLeft>
          {!isViewMode && (
            <Icon
              style={{ marginRight: '8px' }}
              component={DeleteSvg}
              activeComponent={DeleteActiveSvg}
              onClick={(e) => {
                onDeleteRuleGroup?.();
                e.stopPropagation();
              }}
            />
          )}
        </TopBar>
        <Main showPadding={showPadding}>
          {items}
          {active ? (
            <AddRuleButtonWrapper>
              <AddRuleButton text="Add OR Rule" onClick={() => onAddOrRuleItem?.()} />
              <AddRuleButton text="Add AND Rule" onClick={() => onAddAndRuleItem?.()} />
            </AddRuleButtonWrapper>
          ) : null}
        </Main>
      </div>
    </DragDropContext>
  );
};

export default RuleGroup;
